import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import * as moment from 'moment-timezone';

export interface TimezoneConfig {
  useLocalTimezone: boolean;
  userTimezone: string;
  userTimezoneAbbr: string;
  pacificTimezone: string;
  pacificTimezoneAbbr: string;
}

@Injectable({
  providedIn: 'root'
})
export class TimezoneService {
  private readonly PACIFIC_TIMEZONE = 'America/Los_Angeles';
  private readonly STORAGE_KEY = 'timezone_preference';

  private timezoneConfig$ = new BehaviorSubject<TimezoneConfig>(this.initializeTimezoneConfig());

  constructor() {
    this.loadPreference();
  }

  private initializeTimezoneConfig(): TimezoneConfig {
    const userTimezone = moment.tz.guess();
    const now = moment();

    return {
      useLocalTimezone: false,
      userTimezone,
      userTimezoneAbbr: moment.tz(now, userTimezone).zoneAbbr(),
      pacificTimezone: this.PACIFIC_TIMEZONE,
      pacificTimezoneAbbr: moment.tz(now, this.PACIFIC_TIMEZONE).zoneAbbr()
    };
  }

  private loadPreference(): void {
    const saved = localStorage.getItem(this.STORAGE_KEY);
    if (saved === 'local') {
      this.setUseLocalTimezone(true);
    }
  }

  private savePreference(useLocal: boolean): void {
    localStorage.setItem(this.STORAGE_KEY, useLocal ? 'local' : 'pacific');
  }

  getTimezoneConfig() {
    return this.timezoneConfig$.asObservable();
  }

  getCurrentConfig(): TimezoneConfig {
    return this.timezoneConfig$.value;
  }

  setUseLocalTimezone(useLocal: boolean): void {
    const config = this.timezoneConfig$.value;
    this.timezoneConfig$.next({ ...config, useLocalTimezone: useLocal });
    this.savePreference(useLocal);
  }

  toggleTimezone(): void {
    const current = this.timezoneConfig$.value.useLocalTimezone;
    this.setUseLocalTimezone(!current);
  }

  formatDate(date: string | Date | null | undefined, format: string = 'MMM D, YYYY, h:mm:ss A'): string {
    if (!date) {
      return '';
    }

    const config = this.timezoneConfig$.value;
    const targetTimezone = config.useLocalTimezone ? config.userTimezone : config.pacificTimezone;
    const timezoneAbbr = config.useLocalTimezone ? config.userTimezoneAbbr : config.pacificTimezoneAbbr;

    const formattedDate = moment.utc(date).tz(targetTimezone).format(format);
    return `${formattedDate} ${timezoneAbbr}`;
  }

  getCurrentTimezoneAbbr(): string {
    const config = this.timezoneConfig$.value;
    return config.useLocalTimezone ? config.userTimezoneAbbr : config.pacificTimezoneAbbr;
  }

  getLocalTimezoneAbbr(): string {
    return this.timezoneConfig$.value.userTimezoneAbbr;
  }

  getPacificTimezoneAbbr(): string {
    return this.timezoneConfig$.value.pacificTimezoneAbbr;
  }

  isUsingLocalTimezone(): boolean {
    return this.timezoneConfig$.value.useLocalTimezone;
  }

  getUserTimezoneName(): string {
    return this.timezoneConfig$.value.userTimezone;
  }

  isDifferentFromPacific(): boolean {
    return this.timezoneConfig$.value.userTimezone !== this.PACIFIC_TIMEZONE;
  }
}
